import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormLabel from '@material-ui/core/FormLabel';
import { get } from 'helpers/apiHelpers';
import CardBody from 'components/Card/CardBody';
import CardWrapper from 'components/Card/CardWrapper';
import InfoIconTooltip from 'components/InfoIconTooltip/InfoIconTooltip';
import DataGrid from 'components/DataGrid/DataGrid';
import FormControlStickyButton from 'components/FormControlStickyButton/FormControlStickyButton';
import FormImageUpload from 'components/FormImageUpload/FormImageUpload';
import FormSelectDropdown from 'components/FormSelect/FormSelectDropdown';
import FormSelectSingle from 'components/FormSelect/FormSelectSingle';
import FormTags from 'components/FormTags/FormTags';
import FormTextInput from 'components/FormTextInput/FormTextInput';
import FormTextInputTranslatable from 'components/FormTextInput/FormTextInputTranslatable';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import LogView from 'components/History/LogView';
import ReactSelect from 'components/ReactSelect/ReactSelect';
import { isNull } from 'lodash';
import BooleanView from 'components/DataGrid/ValueViews/BooleanView';
import DateFilter from 'components/Grid/Filter/Date';
import Select from 'components/Grid/Filter/Select';

import IngredientsDetails from './IngredientsDetails';

const mapEmployeeToLabel = employee =>
  `${employee.firstName} ${employee.lastName} (${employee.email})`;

const RegularRecipeForm = ({
  tags,
  type,
  filter,
  classes,
  recipeId,
  getImage,
  handleWpOM,
  removeImage,
  recipeState,
  handleSubmit,
  getAllergens,
  selectedTags,
  handleChange,
  workNameState,
  addIngredient,
  defaultWorker,
  selectEmployee,
  handleQuantity,
  handleUserValue,
  handleTagChange,
  userIngredients,
  removeIngredient,
  kitchenImageStateUrl,
  handleThermalProcessing,
  handleWorkingOnMachining,
  departmentState,
  kitchenState,
  commonWotValue,
  isCommonWot,
  handleClickCommonWot,
  handleChangeCommonWotValue,
  nameForClient,
}) => {
  const { t } = useTranslation();
  const [kitchens, setKitchens] = useState([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- FIXME
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);

  const fetchEmployeesList = async () => {
    try {
      const response = await get(
        `/employees/list?recipeProductionHistoryId=${recipeId}`,
        { partial: false }
      );

      setEmployees(
        (response?.['hydra:member'] || []).map(
          ({ firstName, lastName, '@id': iri, image }) => ({
            '@id': iri,
            value: `${firstName} ${lastName}`,
            contentUrl: image?.contentUrl,
          })
        )
      );
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-empty -- FIXME
    } catch (error) {}
  };

  const fetchKitchens = async () => {
    try {
      const response = await get('/kitchens');

      setKitchens(response?.['hydra:member'] || []);
    } catch (error) {
      // eslint-disable-next-line no-console -- FIXME
      console.log({ error });
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await get('/departments');

      setDepartments(response?.['hydra:member'] || []);
    } catch (error) {
      // eslint-disable-next-line no-console -- FIXME
      console.log({ error });
    }
  };

  const fetchEmployees = async value => {
    try {
      const employeesResponse = await get('/employees', {
        pageSize: 30,
        page: 1,
        sorted: [],
        _orX: [
          [{ id: value, firstName: value, lastName: value, email: value }],
        ],
      });

      return employeesResponse['hydra:member'];
    } catch (error) {
      // eslint-disable-next-line no-console -- FIXME
      console.error(error);
      return [];
    }
  };

  useEffect(() => {
    fetchKitchens();
    fetchDepartments();

    if (recipeId) {
      fetchEmployeesList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, []);

  const options = [
    { name: t('common.all'), '@id': 'all' },
    {
      name: t('recipe.field.publicOnMenuPlanner.yes'),
      '@id': false,
    },
    {
      name: t('recipe.field.publicOnMenuPlanner.no'),
      '@id': true,
    },
  ];

  return (
    <>
      <CardWrapper title={t('form.basicInfo')}>
        <CardBody>
          <form>
            <GridContainer justify="center">
              <GridItem md={12}>
                <GridContainer>
                  <GridItem md={4}>
                    <FormTextInputTranslatable
                      label={`${t('form.field.nameForClient')}*`}
                      name="nameForClient"
                      value={nameForClient}
                      handleChange={handleChange}
                    />
                  </GridItem>
                  <GridItem md={4}>
                    <FormTextInput
                      label={
                        <InfoIconTooltip
                          title={t('$*recipe.form.emptyWorkingName')}
                        >
                          {t('form.field.workingName')}
                        </InfoIconTooltip>
                      }
                      classes={classes}
                      name="workNameState"
                      value={workNameState}
                      handleChange={handleChange}
                      inputSize={12}
                    />
                  </GridItem>
                  <GridItem md={4}>
                    <GridContainer>
                      <GridItem md={12} style={{ marginTop: '-3px' }}>
                        <FormLabel className={classes.labelHorizontal}>
                          {t('form.field.tags.description')}
                        </FormLabel>
                        <ReactSelect
                          selectedValues={selectedTags}
                          options={tags}
                          handleChange={handleTagChange}
                        />
                      </GridItem>
                    </GridContainer>
                  </GridItem>
                  <GridItem md={4}>
                    <FormSelectSingle
                      classes={classes}
                      label={t('form.field.type')}
                      options={[
                        {
                          label: `${t('common.lack')[0].toUpperCase()}${t(
                            'common.lack'
                          ).slice(1)}`,
                          value: null,
                        },
                        {
                          label: t('form.field.type.WEIGHT'),
                          value: 'WEIGHT',
                        },
                        {
                          label: t('form.field.type.PIECE'),
                          value: 'PIECE',
                        },
                      ]}
                      value={type}
                      mapBy="label"
                      trackBy="value"
                      name="type"
                      handleChange={handleChange}
                      id="type"
                      allowDeselect={true}
                    />
                  </GridItem>
                  <GridItem md={4}>
                    <FormSelectSingle
                      classes={classes}
                      label={t('form.field.kitchen')}
                      options={kitchens}
                      value={kitchenState}
                      mapBy="value"
                      trackBy="@id"
                      name="kitchenState"
                      handleChange={handleChange}
                      id="kitchen"
                      allowDeselect={true}
                    />
                  </GridItem>
                  <GridItem md={4}>
                    <FormSelectSingle
                      classes={classes}
                      label={t('form.field.department')}
                      options={departments}
                      value={departmentState}
                      mapBy="value"
                      trackBy="@id"
                      name="departmentState"
                      handleChange={handleChange}
                      id="department"
                      allowDeselect={true}
                    />
                  </GridItem>
                  <GridItem md={4}>
                    <FormLabel className={classes.labelHorizontal}>
                      {t('form.field.defaultWorker')}
                    </FormLabel>
                    <FormSelectDropdown
                      handleClick={selectEmployee}
                      placeholder={t('form.field.search')}
                      filter={fetchEmployees}
                      filterName={mapEmployeeToLabel}
                      filtervalue="@id"
                      includeWorkName={false}
                      value={
                        defaultWorker
                          ? {
                              value: defaultWorker['@id'],
                              label: mapEmployeeToLabel(defaultWorker),
                            }
                          : null
                      }
                      includeId={true}
                    />
                  </GridItem>
                </GridContainer>
              </GridItem>
            </GridContainer>
          </form>
        </CardBody>
      </CardWrapper>
      <CardWrapper title={t('form.field.ingredients')}>
        <CardBody>
          <GridContainer justify="center">
            <GridItem md={12}>
              <FormSelectDropdown
                handleClick={addIngredient}
                placeholder={t('form.field.search')}
                filter={filter}
                includeWorkName={true}
                clearAfterSelect={true}
                includeId={true}
              />
              <IngredientsDetails
                data={userIngredients}
                classes={classes}
                handleUserValue={handleUserValue}
                handleQuantity={handleQuantity}
                handleRemove={removeIngredient}
                handleWpOM={handleWpOM}
                handleThermalProcessing={handleThermalProcessing}
                handleWorkingOnMachining={handleWorkingOnMachining}
                commonWotValue={commonWotValue}
                isCommonWot={isCommonWot}
                handleClickCommonWot={handleClickCommonWot}
                handleChangeCommonWotValue={handleChangeCommonWotValue}
              />
            </GridItem>
            <GridItem md={6}>
              <FormTextInput
                label={t('form.field.recipe')}
                classes={classes}
                name="recipeState"
                value={recipeState}
                handleChange={handleChange}
                inputSize={12}
                multiline={true}
                rows={3}
                rowsMax={20}
                maxLength={5000}
              />
            </GridItem>
            <GridItem md={6}>
              <FormTags
                label={t('form.field.allergens')}
                classes={classes}
                tagProps={{
                  className:
                    'react-tagsinput-tag react-tagsinput-tag-small react-tagsinput-tag-disabled danger',
                }}
                state={getAllergens}
                disabled={true}
              />
            </GridItem>
          </GridContainer>
        </CardBody>
      </CardWrapper>
      <CardWrapper title={t('form.additionalInfo')}>
        <CardBody>
          <GridContainer justify="center">
            <GridItem md={12}>
              <GridContainer>
                <GridItem md={6}>
                  <FormImageUpload
                    label={t('form.field.kitchenImage')}
                    stateName="kitchenImageState"
                    getImage={getImage}
                    removeImage={removeImage}
                    previewUrl={kitchenImageStateUrl}
                  />
                </GridItem>
              </GridContainer>
            </GridItem>
          </GridContainer>
        </CardBody>
      </CardWrapper>

      <CardWrapper title={t('$*recipe.form.occurrenceInDishes')}>
        <CardBody>
          <DataGrid
            actions={false}
            remove={false}
            hideArchive={true}
            paginationTop={true}
            paginationBottom={false}
            defaultSorted={[{ id: 'date', desc: true }]}
            url={`/recipes/${recipeId}/menu-planner-usage`}
            manipulateQuery={(requestData, query) => {
              if (query.draft === 'all') {
                delete query.draft;
              }
              return query;
            }}
            columns={[
              {
                title: 'ID',
                accessor: 'dish.id',
                name: 'dish.id',
                filterable: true,
                sortable: true,
                width: 100,
              },
              {
                title: 'form.field.name',
                accessor: row => (
                  <a
                    href={`/admin/meals/edit/${row.dish.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {row.dish.name}
                  </a>
                ),

                name: 'dish.nameForClient',
                filterable: true,
                sortable: true,
                width: 300,
              },
              {
                title: 'form.field.diet',
                accessor: row => {
                  if (isNull(row.draft)) {
                    return '-';
                  }
                  return row.diet.name;
                },
                name: 'diet.name',
                filterable: true,
                sortable: true,
              },
              {
                title: 'form.field.variant',
                accessor: row => {
                  if (isNull(row.draft)) {
                    return '-';
                  }
                  return row.variant.name;
                },
                name: 'variant.name',
                filterable: true,
                sortable: true,
              },
              {
                title: 'form.field.brand',
                accessor: 'brand.name',
                name: 'brand.name',
                filterable: true,
                sortable: true,
              },
              {
                title: 'form.field.publicInMenuPlanner',
                accessor: row => {
                  if (isNull(row.draft)) {
                    return '-';
                  }
                  return <BooleanView value={!row.draft} />;
                },
                name: 'draft',
                filterable: true,
                sortable: true,
                Filter: ({ onChange, filter: dataFilter }) => (
                  <Select
                    onChange={onChange}
                    value={{
                      '@id': dataFilter?.value,
                    }}
                    options={options}
                    multiple={false}
                  />
                ),
              },
              {
                title: 'form.field.date',
                accessor: row => {
                  if (isNull(row.draft)) {
                    return '-';
                  }
                  return row.date;
                },
                name: 'date',
                filterable: true,
                sortable: true,
                Filter: ({ onChange, filter: dateFilter }) => (
                  <DateFilter onChange={onChange} value={dateFilter?.value} />
                ),
              },
            ]}
          />
        </CardBody>
      </CardWrapper>

      {!recipeId && (
        <FormControlStickyButton
          classes={classes}
          discardText={t('form.cancel')}
          submitText={t('form.save')}
          cancelPath="/admin/recipes"
          handleSubmit={handleSubmit}
        />
      )}
      {recipeId && (
        <LogView
          classes={classes}
          discardText={t('form.cancel')}
          submitText={t('form.save')}
          cancelPath="/admin/recipes"
          handleSubmit={handleSubmit}
          iri={`/recipes/${recipeId}`}
        />
      )}
    </>
  );
};

export default RegularRecipeForm;
